import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { toastStore, type IDropdownItem, type IIconsVector } from 'shared/ui'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationKey } from 'entities/Integrations'
import { IntegrationsApi } from 'entities/Integrations/api/integrations'
import { ClientsApi } from 'entities/Clients/api/clients'
import { type IResponseClient } from 'entities/Clients/api/types'
import { TeamsSelectFieldStore } from 'pages/clients/ui/shared/TeamsSelectField/store/teamsSelectFieldStore'
import { ClientInviteFieldStore } from 'pages/clients/pages/create/ui/ClientDetails/ClientInviteField/store/clientInviteFieldStore'

type ICreateOption = { value: string; title: string; icon: IIconsVector }

export class ClientDetailsStore {
  private _selectedType = 'new'
  private _useExistingIntegration = false
  private _selectedIntegrationKey: string | number | null = null
  isRefreshLoading = false
  teamsLoading = false
  teamsSelectFieldStore: TeamsSelectFieldStore
  clientInviteFieldStore: ClientInviteFieldStore
  validateLoading = false
  clientList: IResponseClient[] = []
  clientsLoading = false
  selectedDuplicateClientId: number | null = null
  isDuplicateTableAction = false

  constructor(private _entities: IEntitiesStore, private _duplicateId?: number) {
    this.teamsSelectFieldStore = new TeamsSelectFieldStore()
    this.clientInviteFieldStore = new ClientInviteFieldStore()
    if (this._duplicateId) {
      this.selectedDuplicateClientId = this._duplicateId
      this.isDuplicateTableAction = true
      this.initDuplicateClient()
    }
    makeAutoObservable(this)
  }

  init = async () => {
    await this._entities.integrationsStore.fetchIntegrations()
    await this.clientInviteFieldStore.loadHubSpotUsers()
  }

  initDuplicateClient = () => {
    this._selectedType = 'duplicate'
    this.loadAllClients()
  }

  get createOptions(): ICreateOption[] {
    return [
      {
        value: 'new',
        title: 'New',
        icon: 'plusCircle',
      },
      {
        value: 'duplicate',
        title: 'Duplicate',
        icon: 'copy',
      },
    ]
  }

  get selectedType() {
    return this._selectedType
  }

  get isDuplicateType() {
    return this._selectedType === 'duplicate'
  }

  get useExistingIntegration() {
    return this._useExistingIntegration
  }

  setSelectedType = (type: string) => {
    this._selectedType = type

    if (this.isDuplicateType) {
      this.loadAllClients()
    } else {
      this.selectedDuplicateClientId = null
    }
  }

  setUseExistingIntegration = async (value: boolean) => {
    this._useExistingIntegration = value
    if (value && this.isHubspotConnected) {
      this.loadHubSpotTeams()
    } else {
      this._selectedIntegrationKey = null
      this.teamsSelectFieldStore.reset()
    }
  }

  get selectedIntegrationKey() {
    return this._selectedIntegrationKey
  }

  get hubspotIntegration() {
    return this._entities.integrationsStore.getIntegration(IntegrationKey.hubspot)
  }

  get isHubspotConnected() {
    return this.hubspotIntegration?.isConnected ?? false
  }

  get integrationSelectItems() {
    const items: IDropdownItem[] = [
      {
        id: 'select',
        label: 'Select integration',
      },
    ]
    if (this.hubspotIntegration && this.isHubspotConnected) {
      items.push({
        id: IntegrationKey.hubspot,
        label: this.hubspotIntegration?.name,
        iconL: this.hubspotIntegration?.icon,
      })
    }
    return items
  }

  get selectedIntegrationItem() {
    return (
      this.integrationSelectItems.find((item) => item.id === this._selectedIntegrationKey) ?? null
    )
  }

  get isHubSpotSelected() {
    return this._selectedIntegrationKey === IntegrationKey.hubspot
  }

  get hasNoConnectedIntegration() {
    return this._useExistingIntegration && !this.isHubspotConnected
  }

  get isCreateTypeShouldShow() {
    return !this._duplicateId
  }

  get clientListDropdownItems() {
    return this.clientList
      .filter((client) => client.name)
      .map((client) => ({
        id: client.id,
        label: client.name,
      }))
  }

  setSelectedIntegration = (value: string) => {
    this._selectedIntegrationKey = value
    if (value !== IntegrationKey.hubspot) {
      this.teamsSelectFieldStore.reset()
    }
  }

  setSelectedDuplicateClient = (value: number) => {
    this.selectedDuplicateClientId = value
  }

  loadHubSpotTeams = async () => {
    runInAction(() => {
      this.teamsLoading = true
    })
    try {
      const { data } = await ClientsApi.getHubSpotTeams()
      runInAction(() => {
        this.teamsSelectFieldStore.setTeamsList(data)
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this.teamsLoading = false
      })
    }
  }

  activateRefresh = async () => {
    runInAction(() => {
      this.isRefreshLoading = true
    })
    try {
      await Promise.all([
        IntegrationsApi.activateIntegrationRefresh(IntegrationKey.hubspot),
        this._entities.integrationsStore.fetchIntegrations(),
        this.loadHubSpotTeams(),
      ])

      this.teamsSelectFieldStore.setSelectedTeamIds([])
      toastStore.add({
        type: 'success',
        title: 'HubSpot data refreshed',
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this.isRefreshLoading = false
      })
    }
  }

  validate = async (onSuccess: () => void) => {
    const email =
      this.clientInviteFieldStore.inputValue || this.clientInviteFieldStore.selectedEmail

    if (!email) {
      this.clientInviteFieldStore.setEmailError('The email field is required')
      return
    }
    try {
      runInAction(() => {
        this.validateLoading = true
      })
      const { data } = await ClientsApi.validateInvite(email)
      if (data.valid) {
        return onSuccess()
      }
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        const emailMessage = e.errors.getError('email')
        if (emailMessage) {
          this.clientInviteFieldStore.setEmailError(emailMessage)
        } else {
          toastStore.add({
            type: 'error',
            title: 'Something went wrong, please try again',
          })
        }
      }
    } finally {
      runInAction(() => {
        this.validateLoading = false
      })
    }
  }

  loadAllClients = async () => {
    if (this.clientList.length > 0) return

    runInAction(() => {
      this.clientsLoading = true
    })
    try {
      const { data } = await ClientsApi.getAllClients()
      runInAction(() => {
        this.clientList = data
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this.clientsLoading = false
      })
    }
  }

  reset = () => {
    this._selectedIntegrationKey = null
    this._useExistingIntegration = false
    this._selectedType = 'new'
    this.teamsSelectFieldStore.reset()
    this.clientInviteFieldStore.reset()
    this.clientList = []
    this.selectedDuplicateClientId = null
    this.isDuplicateTableAction = false
  }
}
